import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { movieService, notificationService } from '../../services/api';
import { Movie, Notification } from '../../types';
import './Header.css';

const Header: React.FC = () => {
  const { user, subscription, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [isMobileNotificationOpen, setIsMobileNotificationOpen] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Movie[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setIsNotificationPanelOpen(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Load notifications
  useEffect(() => {
    if (isAuthenticated) {
      loadNotifications();
      const interval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const loadNotifications = async () => {
    const data = await notificationService.getNotifications();
    setNotifications(data.notifications);
    setUnreadCount(data.unread_count);
  };

  const loadUnreadCount = async () => {
    const count = await notificationService.getUnreadCount();
    setUnreadCount(count);
  };

  // Search functionality
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      const results = await movieService.searchMovies(searchQuery);
      setSearchResults(results.slice(0, 8));
      setShowSearchResults(true);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    document.body.classList.toggle('menu-open', !isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    document.body.classList.remove('menu-open');
  };

  const handleMarkAllRead = async () => {
    await notificationService.markAllAsRead();
    loadNotifications();
  };

  const formatNotificationTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
    return date.toLocaleDateString();
  };

  const getThumbnailUrl = (thumbnail: string) => {
    if (thumbnail.startsWith('http://') || thumbnail.startsWith('https://')) {
      return thumbnail;
    }
    return `/public/uploads/thumbnails/${thumbnail}`;
  };

  return (
    <>
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 border-b border-white/5 ${isScrolled ? 'bg-black/98 backdrop-blur-lg' : 'bg-gradient-to-b from-black/95 to-black/80 backdrop-blur-lg'}`}>
        <nav className="container mx-auto px-2 sm:px-3 lg:px-4 py-2 lg:py-3">
          <div className="flex items-center justify-between w-full gap-3 lg:gap-6">
            {/* Logo Section */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <img 
                src="/public/assets/images/cineclubs.png" 
                alt="CineClubs Logo" 
                className="h-10 sm:h-12 md:h-14 lg:h-16 w-auto object-contain"
              />
              <div className="hidden sm:flex flex-col justify-center">
                <span className="font-black text-base sm:text-lg md:text-xl lg:text-2xl tracking-tight leading-tight cineclubs-logo-text">
                  CINECLUBS
                </span>
                <span className="text-white text-[8px] sm:text-[10px] lg:text-xs font-bold uppercase tracking-wider">
                  STREAM TOGETHER
                </span>
              </div>
            </Link>

            {/* Navigation - Center */}
            <div className="flex-1 flex justify-center lg:justify-start lg:ml-4 xl:ml-8">
              <ul className="hidden lg:flex items-center gap-6 xl:gap-10 font-semibold text-sm lg:text-base">
                <li>
                  <Link to="/streams" className="hover:text-netflix-red transition-colors whitespace-nowrap px-2 flex items-center gap-2">
                    <i className="fas fa-circle text-red-500 text-xs animate-pulse"></i>
                    Live Streams
                  </Link>
                </li>
                <li>
                  <Link to="/movies" className="hover:text-netflix-red transition-colors whitespace-nowrap px-2">
                    Movies
                  </Link>
                </li>
                <li>
                  <Link to="#" className="hover:text-netflix-red transition-colors whitespace-nowrap px-2">
                    TV Series
                  </Link>
                </li>
              </ul>
            </div>

            {/* Search Box - Hidden on mobile */}
            <div className="hidden md:block search-container relative" style={{ width: '200px', minWidth: '180px' }}>
              <input
                type="text"
                className="search-input w-full h-10 bg-[rgba(20,20,20,0.95)] border-2 border-[rgba(229,9,20,0.5)] rounded-full px-3 pr-10 text-white text-sm outline-none transition-colors focus:border-[rgba(229,9,20,0.8)]"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
                autoComplete="off"
              />
              <i className="fas fa-search absolute right-4 top-1/2 -translate-y-1/2 text-netflix-red pointer-events-none"></i>
              
              {/* Search Results Dropdown */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="search-results absolute top-full left-0 right-0 mt-2 max-h-96 overflow-y-auto bg-[rgba(20,20,20,0.98)] border border-[rgba(229,9,20,0.3)] rounded-xl backdrop-blur-lg shadow-xl z-50">
                  {searchResults.map((movie) => (
                    <Link
                      key={movie.id}
                      to={`/movies/${movie.id}`}
                      className="search-result-item flex items-center gap-3 p-3 hover:bg-white/10 transition-colors border-b border-white/5 last:border-b-0"
                      onClick={() => {
                        setShowSearchResults(false);
                        setSearchQuery('');
                      }}
                    >
                      <img
                        src={getThumbnailUrl(movie.thumbnail)}
                        alt={movie.title}
                        className="w-12 h-16 object-cover rounded"
                        onError={(e) => { (e.target as HTMLImageElement).src = '/public/assets/images/no-image.jpg'; }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm font-semibold truncate">{movie.title}</div>
                        <div className="text-white/60 text-xs flex gap-2">
                          <span><i className="fas fa-calendar mr-1"></i>{movie.release_year || movie.year || 'N/A'}</span>
                          <span><i className="fas fa-star text-yellow-400 mr-1"></i>{movie.rating || 'N/A'}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* User Info - Right */}
            <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 flex-shrink-0">
              {/* Mobile: Subscribe Button */}
              <Link
                to="/subscribe"
                className="sm:hidden flex items-center justify-center w-8 h-8 bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-full"
                aria-label="Subscribe"
              >
                <i className="fas fa-crown text-white text-xs"></i>
              </Link>

              {/* Mobile: Notification Bell */}
              <div className="notification-bell-container sm:hidden relative">
                <button
                  className="notification-bell relative p-2 rounded-full hover:bg-[rgba(229,9,20,0.1)] transition-colors"
                  onClick={() => setIsMobileNotificationOpen(!isMobileNotificationOpen)}
                  aria-label="Notifications"
                >
                  <i className="fas fa-bell text-white text-lg"></i>
                  {unreadCount > 0 && (
                    <span className="notification-badge absolute -top-0.5 -right-0.5 bg-gradient-to-r from-netflix-red to-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
              </div>

              {/* Desktop: Notification Bell */}
              <div className="notification-bell-container hidden sm:block relative" ref={notificationRef}>
                <button
                  className="notification-bell relative p-2 rounded-full hover:bg-[rgba(229,9,20,0.1)] transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsNotificationPanelOpen(!isNotificationPanelOpen);
                    if (!isNotificationPanelOpen) loadNotifications();
                  }}
                  aria-label="Notifications"
                >
                  <i className="fas fa-bell text-white text-xl"></i>
                  {unreadCount > 0 && (
                    <span className="notification-badge absolute -top-0.5 -right-0.5 bg-gradient-to-r from-netflix-red to-red-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center animate-pulse">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Panel */}
                {isNotificationPanelOpen && (
                  <div className="notification-panel absolute top-full right-0 mt-2 w-96 max-h-[500px] bg-[rgba(20,20,20,0.98)] border border-[rgba(229,9,20,0.3)] rounded-xl backdrop-blur-lg shadow-xl overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-white/10 flex justify-between items-center bg-[rgba(229,9,20,0.05)]">
                      <h3 className="text-white font-bold">🔔 Notifications</h3>
                      <button
                        onClick={handleMarkAllRead}
                        className="text-netflix-red text-sm font-semibold hover:bg-[rgba(229,9,20,0.1)] px-2 py-1 rounded"
                      >
                        Mark all read
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto max-h-96">
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center text-white/50">
                          <i className="fas fa-bell-slash text-4xl mb-3 opacity-30"></i>
                          <p>No notifications yet</p>
                        </div>
                      ) : (
                        notifications.map((notif) => (
                          <div
                            key={notif.id}
                            className={`p-4 border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors ${
                              !notif.is_read ? 'bg-[rgba(229,9,20,0.05)] border-l-2 border-l-netflix-red' : ''
                            }`}
                            onClick={async () => {
                              await notificationService.markAsRead(notif.id);
                              loadNotifications();
                              if (notif.link) navigate(notif.link);
                            }}
                          >
                            <div className="flex gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[rgba(229,9,20,0.2)] to-[rgba(255,10,22,0.1)] flex items-center justify-center flex-shrink-0">
                                <i className={`fas ${notif.icon || 'fa-bell'} text-netflix-red`}></i>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-white font-semibold text-sm">{notif.title}</div>
                                <div className="text-white/60 text-xs mt-1 line-clamp-2">{notif.message}</div>
                                <div className="text-white/40 text-xs mt-1">{formatNotificationTime(notif.created_at)}</div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Desktop: User Section */}
              {isAuthenticated && user ? (
                <div className="user-info desktop-user-section hidden sm:flex items-center gap-3 lg:gap-4">
                  {/* Subscription Badge */}
                  {subscription?.has_subscription ? (
                    subscription.is_trial ? (
                      <Link
                        to="/subscribe"
                        className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-lg font-bold text-sm transition-all hover:from-yellow-500 hover:to-yellow-700"
                      >
                        🎁 Trial: {subscription.trial_days_remaining} days
                      </Link>
                    ) : (
                      <Link
                        to="/subscribe"
                        className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-green-500 to-green-700 text-white rounded-lg font-bold text-sm transition-all hover:from-green-600 hover:to-green-800"
                      >
                        ✓ Premium Active
                      </Link>
                    )
                  ) : (
                    <Link
                      to="/subscribe"
                      className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-red-500 to-red-700 text-white rounded-lg font-bold text-sm transition-all hover:from-red-600 hover:to-red-800"
                    >
                      🔒 Subscribe Now
                    </Link>
                  )}

                  {/* Streaming Button (for streamers) */}
                  {user.is_streamer && (
                    <Link
                      to={`/stream/${user.stream_slug || user.username}`}
                      className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-semibold transition-all shadow-lg streaming-button"
                    >
                      <i className="fas fa-broadcast-tower"></i>
                      <span className="hidden lg:inline">Streaming</span>
                    </Link>
                  )}

                  {/* Coins Button */}
                  <Link
                    to="/coins"
                    className="flex items-center gap-2 px-4 py-2 bg-netflix-red/15 border border-netflix-red hover:bg-netflix-red/25 rounded-lg font-semibold transition-colors text-white"
                  >
                    <i className="fas fa-coins"></i>
                    <span>{user.coins || 0}</span>
                  </Link>

                  {/* User Dropdown */}
                  <div className="user-dropdown relative" ref={dropdownRef}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsUserDropdownOpen(!isUserDropdownOpen);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg font-semibold transition-colors"
                    >
                      <i className="fas fa-user-circle"></i>
                      <span>{user.username}</span>
                      <i className="fas fa-chevron-down text-xs"></i>
                    </button>

                    {isUserDropdownOpen && (
                      <div className="dropdown-menu absolute top-full right-0 mt-2 min-w-[220px] bg-[rgba(20,20,20,0.98)] border border-[rgba(229,9,20,0.3)] rounded-xl backdrop-blur-lg shadow-xl overflow-hidden">
                        {user.role === 'admin' && (
                          <>
                            <Link
                              to="/admin"
                              className="flex items-center gap-3 px-4 py-3 hover:bg-white/10 transition-colors text-purple-400"
                              onClick={() => setIsUserDropdownOpen(false)}
                            >
                              <i className="fas fa-crown"></i> Admin Panel
                            </Link>
                            <div className="border-t-2 border-dashed border-white/20 my-2"></div>
                          </>
                        )}
                        {user.is_streamer && (
                          <Link
                            to={`/stream/${user.stream_slug || user.username}`}
                            className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/10 transition-colors border-b border-dashed border-white/10 bg-gradient-to-r from-[rgba(229,9,20,0.1)] to-[rgba(255,10,22,0.1)]"
                            onClick={() => setIsUserDropdownOpen(false)}
                          >
                            <i className="fas fa-broadcast-tower text-red-500"></i> My Stream Room
                          </Link>
                        )}
                        <Link
                          to="/profile"
                          className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/10 transition-colors border-b border-dashed border-white/10"
                          onClick={() => setIsUserDropdownOpen(false)}
                        >
                          <i className="fas fa-user"></i> Profile
                        </Link>
                        <Link
                          to="/settings"
                          className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/10 transition-colors border-b border-dashed border-white/10"
                          onClick={() => setIsUserDropdownOpen(false)}
                        >
                          <i className="fas fa-cog"></i> Settings
                        </Link>
                        <button
                          onClick={() => {
                            setIsUserDropdownOpen(false);
                            handleLogout();
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-900/20 transition-colors text-red-500 border-b-2 border-dashed border-white/20"
                        >
                          <i className="fas fa-sign-out-alt"></i> Logout
                        </button>
                        <div className="bg-black/30 p-3 flex items-center gap-2 justify-center rounded-b-xl">
                          <img
                            src="/public/assets/images/logoformovies.png"
                            alt="Logo"
                            className="w-8 h-8 object-contain filter brightness-130"
                          />
                          <span className="text-white font-bold text-sm tracking-wide">CINECLUBS</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="hidden sm:block">
                  <button
                    onClick={() => navigate('/login')}
                    className="px-6 py-2 bg-netflix-red hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    Login
                  </button>
                </div>
              )}

              {/* Mobile: Coins display */}
              {isAuthenticated && user && (
                <div className="mobile-user-info sm:hidden flex items-center gap-2">
                  <Link
                    to="/coins"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 bg-netflix-red/15 border border-netflix-red hover:bg-netflix-red/25 rounded-lg text-xs font-bold transition-colors text-white"
                  >
                    <i className="fas fa-coins"></i>
                    <span>{user.coins || 0}</span>
                  </Link>
                </div>
              )}

              {/* Mobile Menu Toggle */}
              <button
                className="mobile-menu-toggle lg:hidden text-white p-1.5 sm:p-2 hover:bg-white/10 rounded transition-colors flex-shrink-0 border border-white/20 flex items-center justify-center w-10 h-10"
                onClick={toggleMobileMenu}
                aria-label="Toggle menu"
              >
                <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'} text-base sm:text-lg`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          <div
            className={`nav-links fixed top-14 sm:top-16 left-0 right-0 bg-black/95 backdrop-blur-lg border-t border-white/10 shadow-2xl transform transition-all duration-300 ease-in-out lg:hidden z-40 ${
              isMobileMenuOpen ? 'translate-y-0 opacity-100 pointer-events-auto' : '-translate-y-full opacity-0 pointer-events-none'
            }`}
            style={{ maxHeight: 'calc(100vh - 60px)', overflowY: 'auto' }}
          >
            <ul className="flex flex-col gap-0 p-4">
              {/* Mobile Search */}
              <li className="w-full mb-3">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search movies..."
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/50 text-sm focus:outline-none focus:border-netflix-red"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <i className="fas fa-search absolute right-4 top-1/2 -translate-y-1/2 text-white/50"></i>
                </div>
              </li>

              {/* Main Navigation */}
              <li className="w-full">
                <Link
                  to="/streams"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/10 transition-colors text-white text-sm font-medium border-b border-white/10"
                  onClick={closeMobileMenu}
                >
                  <i className="fas fa-circle text-red-500 animate-pulse"></i>
                  <span>Live Streams</span>
                </Link>
              </li>
              <li className="w-full">
                <Link
                  to="/movies"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/10 transition-colors text-white text-sm font-medium border-b border-white/10"
                  onClick={closeMobileMenu}
                >
                  <i className="fas fa-film text-netflix-red"></i>
                  <span>Movies</span>
                </Link>
              </li>
              <li className="w-full">
                <Link
                  to="#"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/10 transition-colors text-white text-sm font-medium border-b border-white/10"
                  onClick={closeMobileMenu}
                >
                  <i className="fas fa-tv text-netflix-red"></i>
                  <span>TV Series</span>
                </Link>
              </li>

              {/* Mobile Auth Section */}
              <div className="mobile-auth-section border-t border-white/10 mt-2 pt-2">
                {isAuthenticated && user ? (
                  <>
                    {/* Subscription Status */}
                    {subscription?.has_subscription ? (
                      subscription.is_trial ? (
                        <li className="w-full">
                          <Link
                            to="/subscribe"
                            className="p-3 bg-gradient-to-r from-yellow-400/20 to-yellow-600/20 border border-yellow-500/30 rounded-lg block"
                            onClick={closeMobileMenu}
                          >
                            <div className="flex items-center gap-2 text-yellow-400 text-xs font-bold">
                              🎁 Trial: {subscription.trial_days_remaining} days left
                            </div>
                          </Link>
                        </li>
                      ) : (
                        <li className="w-full">
                          <Link
                            to="/subscribe"
                            className="p-3 bg-gradient-to-r from-green-500/20 to-green-700/20 border border-green-500/30 rounded-lg block"
                            onClick={closeMobileMenu}
                          >
                            <div className="flex items-center gap-2 text-green-400 text-xs font-bold">
                              ✓ Premium Active
                            </div>
                          </Link>
                        </li>
                      )
                    ) : (
                      <li className="w-full">
                        <Link
                          to="/subscribe"
                          className="flex items-center gap-3 p-3 rounded-lg bg-netflix-red/20 hover:bg-netflix-red/30 transition-colors text-white text-sm font-bold border-b border-white/10"
                          onClick={closeMobileMenu}
                        >
                          <i className="fas fa-crown"></i>
                          <span>🔒 Subscribe Now</span>
                        </Link>
                      </li>
                    )}
                    <li className="w-full">
                      <Link
                        to="/profile"
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/10 transition-colors text-white text-sm font-medium border-b border-white/10"
                        onClick={closeMobileMenu}
                      >
                        <i className="fas fa-user text-netflix-red"></i>
                        <span>{user.username}</span>
                        <span className="ml-auto inline-flex items-center bg-netflix-red/15 px-2 py-0.5 rounded-full text-xs font-bold border border-netflix-red text-white">
                          <i className="fas fa-coins mr-1"></i>
                          {user.coins || 0}
                        </span>
                      </Link>
                    </li>
                    <li className="w-full">
                      <Link
                        to="/settings"
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/10 transition-colors text-white text-sm font-medium border-b border-white/10"
                        onClick={closeMobileMenu}
                      >
                        <i className="fas fa-cog text-netflix-red"></i>
                        <span>Settings</span>
                      </Link>
                    </li>
                    {user.role === 'admin' && (
                      <li className="w-full">
                        <Link
                          to="/admin/movies"
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/10 transition-colors text-white text-sm font-medium border-b border-white/10"
                          onClick={closeMobileMenu}
                        >
                          <i className="fas fa-crown text-yellow-400"></i>
                          <span>Admin Panel</span>
                        </Link>
                      </li>
                    )}
                    {user.is_streamer && (
                      <li className="w-full">
                        <Link
                          to={`/stream/${user.stream_slug || user.username}`}
                          className="flex items-center gap-3 p-3 rounded-lg transition-colors text-white text-sm font-bold border-b border-red-500/30 streaming-button-mobile"
                          onClick={closeMobileMenu}
                        >
                          <i className="fas fa-broadcast-tower text-red-500"></i>
                          <span>🎥 My Stream Room</span>
                        </Link>
                      </li>
                    )}
                    <li className="w-full">
                      <button
                        onClick={() => {
                          closeMobileMenu();
                          handleLogout();
                        }}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-red-600/20 transition-colors text-netflix-red text-sm font-bold"
                      >
                        <i className="fas fa-sign-out-alt"></i>
                        <span>Logout</span>
                      </button>
                    </li>
                  </>
                ) : (
                  <>
                    <li className="w-full">
                      <Link
                        to="/login"
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/10 transition-colors text-white text-sm font-medium border-b border-white/10"
                        onClick={closeMobileMenu}
                      >
                        <i className="fas fa-sign-in-alt text-netflix-red"></i>
                        <span>Login</span>
                      </Link>
                    </li>
                    <li className="w-full">
                      <Link
                        to="/register"
                        className="flex items-center gap-3 p-3 rounded-lg bg-netflix-red/20 hover:bg-netflix-red/30 transition-colors text-white text-sm font-bold"
                        onClick={closeMobileMenu}
                      >
                        <i className="fas fa-user-plus"></i>
                        <span>Create Account</span>
                      </Link>
                    </li>
                  </>
                )}
              </div>
            </ul>
          </div>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 lg:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Notification Panel */}
      {isMobileNotificationOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99998] lg:hidden"
            onClick={() => setIsMobileNotificationOpen(false)}
          />
          <div className="fixed top-14 right-2 left-2 rounded-xl border shadow-2xl z-[99999] lg:hidden bg-gradient-to-b from-[#1a1a1a] to-[#0d0d0d] border-[rgba(229,9,20,0.3)]" style={{ maxHeight: '70vh' }}>
            <div className="flex flex-col" style={{ maxHeight: '70vh' }}>
              <div className="flex items-center justify-between p-3 border-b border-white/10 bg-[rgba(229,9,20,0.1)]">
                <h3 className="text-white font-bold text-sm flex items-center gap-2">
                  <i className="fas fa-bell text-netflix-red"></i> Notifications
                </h3>
                <button
                  onClick={() => setIsMobileNotificationOpen(false)}
                  className="text-white/70 hover:text-white p-1 hover:bg-white/10 rounded-full transition-colors"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="overflow-y-auto p-3" style={{ maxHeight: 'calc(70vh - 100px)' }}>
                {notifications.length === 0 ? (
                  <div className="text-center py-6">
                    <i className="fas fa-bell-slash text-2xl mb-2 text-white/30"></i>
                    <p className="text-sm text-white/50">No notifications yet</p>
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className={`p-3 mb-2 rounded-lg cursor-pointer transition-all hover:scale-[1.02] ${
                        notif.is_read
                          ? 'bg-white/5 border border-white/10'
                          : 'bg-gradient-to-r from-[rgba(229,9,20,0.15)] to-[rgba(229,9,20,0.05)] border border-[rgba(229,9,20,0.3)]'
                      }`}
                      onClick={async () => {
                        await notificationService.markAsRead(notif.id);
                        loadNotifications();
                        setIsMobileNotificationOpen(false);
                        if (notif.link) navigate(notif.link);
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${notif.is_read ? 'bg-white/10' : 'bg-[rgba(229,9,20,0.2)]'}`}>
                          <i className={`fas ${notif.icon || 'fa-bell'} ${notif.is_read ? 'text-white/60' : 'text-netflix-red'}`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-medium text-sm">{notif.title}</div>
                          <div className="text-xs mt-1 text-white/60">{notif.message}</div>
                          <div className="text-xs mt-1.5 text-white/40">{formatNotificationTime(notif.created_at)}</div>
                        </div>
                        {!notif.is_read && <div className="w-2 h-2 rounded-full bg-netflix-red flex-shrink-0 mt-1"></div>}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="p-3 border-t border-white/10">
                <button
                  onClick={() => {
                    handleMarkAllRead();
                    setIsMobileNotificationOpen(false);
                  }}
                  className="w-full py-2.5 bg-gradient-to-r from-netflix-red to-red-700 hover:from-red-600 hover:to-red-800 text-white rounded-lg text-sm font-semibold transition-all shadow-lg"
                >
                  <i className="fas fa-check-double mr-2"></i>Mark All as Read
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Header;
